package com.meistercharts.charts.pv.stringsPlanning

import com.meistercharts.algorithms.layers.AbstractLayer
import com.meistercharts.algorithms.layers.LayerPaintingContext
import com.meistercharts.algorithms.layers.LayerType
import com.meistercharts.algorithms.painter.FillAndStrokeStyle
import com.meistercharts.algorithms.painter.fillAndStroke
import com.meistercharts.annotations.Window
import com.meistercharts.canvas.ChartSupport
import com.meistercharts.canvas.ConfigurationDsl
import com.meistercharts.canvas.DirtyReason
import com.meistercharts.canvas.events.CanvasMouseEventHandler
import com.meistercharts.canvas.events.enabledWhen
import com.meistercharts.canvas.command.CommandExecutor
import com.meistercharts.canvas.interaction.InteractionState
import com.meistercharts.canvas.interaction.ToolReaction
import com.meistercharts.canvas.interaction.HandleRole
import com.meistercharts.canvas.interaction.InteractionTargets
import com.meistercharts.canvas.layout.buffer.BoundsMulti2DBuffer
import com.meistercharts.canvas.layout.buffer.CoordinatesMulti2DBuffer
import com.meistercharts.canvas.layout.buffer.DoubleMulti2DBuffer
import com.meistercharts.canvas.layout.buffer.StringMulti2DBuffer
import com.meistercharts.canvas.paintTextBox
import com.meistercharts.canvas.saved
import com.meistercharts.charts.pv.roofPlanning.Module
import com.meistercharts.charts.pv.roofPlanning.PvRoofPlanningLayer
import com.meistercharts.charts.pv.roofPlanning.PvRoofPlanningLayer.Mode
import com.meistercharts.charts.pv.roofPlanning.PvRoofPlanningModel
import com.meistercharts.charts.pv.solar.LizergyDesign
import com.meistercharts.color.Color
import com.meistercharts.color.RgbaColor
import com.meistercharts.events.EventConsumption
import com.meistercharts.events.EventConsumption.Ignored
import com.meistercharts.font.FontDescriptorFragment
import com.meistercharts.font.FontSize
import com.meistercharts.model.BorderRadius
import com.meistercharts.style.BoxStyle
import it.neckar.events.MouseDownEvent
import it.neckar.events.MouseMoveEvent
import it.neckar.geometry.Direction
import it.neckar.geometry.Rectangle
import it.neckar.open.collections.fastForEach
import it.neckar.open.collections.fastForEachIndexed
import it.neckar.open.dispose.disposeOn
import it.neckar.open.observable.ObservableObject
import it.neckar.open.provider.MultiProvider
import kotlin.math.pow
import kotlin.uuid.Uuid

/**
 * Layer that supports planning of  photo voltaic modules for *one* roof
 */
class PvStringsPlanningLayer(
  val configuration: Configuration,
  additionalConfiguration: Configuration.() -> Unit = {},
) : AbstractLayer() {

  constructor(
    stringsPlanningModel: PvStringsPlanningModel,
    roofPlanningLayer: PvRoofPlanningLayer,
    additionalConfiguration: Configuration.() -> Unit = {},
  ) : this(Configuration(stringsPlanningModel, roofPlanningLayer), additionalConfiguration)

  init {
    configuration.additionalConfiguration()
  }

  override val type: LayerType = LayerType.Content

  /**
   * Decides what the mouse does. Never changes the model - it returns commands.
   */
  private val tool: StringsPlanningTool = StringsPlanningTool()

  /**
   * Applies the commands the tool asks for and keeps the undo history.
   */
  val commandExecutor: CommandExecutor<PvStringsPlanningModel> = CommandExecutor(configuration.stringsPlanningModel)

  private val interactionStateProperty: ObservableObject<InteractionState<Module>> = ObservableObject(InteractionState.empty())

  /**
   * What the user is currently doing with the strings.
   */
  var interactionState: InteractionState<Module> by interactionStateProperty
    private set

  /**
   * The chart support of the running interaction. Set on the first layout, used to mark the canvas as dirty.
   */
  private var chartSupport: ChartSupport? = null

  /**
   * The modules that can be added to or removed from a string.
   *
   * Filled in [layout], queried by the mouse handlers - so hover and press always resolve to the same module.
   */
  private val interactionTargets: InteractionTargets<Module> = InteractionTargets()

  /**
   * Selects the string that is being planned - called by the surrounding UI, not by the canvas.
   */
  fun stringSelected(roofStringUuid: Uuid?) {
    apply(tool.selectString(roofStringUuid, interactionState, configuration.stringsPlanningModel))
  }

  /**
   * Applies what the tool answered: the new interaction state and the model changes it asked for.
   */
  private fun apply(reaction: ToolReaction<Module, PvStringsPlanningModel>) {
    interactionState = reaction.state
    commandExecutor.execute(reaction.commands)

    if (reaction.commands.isNotEmpty()) {
      chartSupport?.markAsDirty(DirtyReason.UserInteraction)
    }
  }

  /**
   * Applies the reaction and answers the event with the consumption the tool asked for.
   */
  private fun handle(reaction: ToolReaction<Module, PvStringsPlanningModel>): EventConsumption {
    apply(reaction)
    return reaction.consumption
  }

  override val mouseEventHandler: CanvasMouseEventHandler = object : CanvasMouseEventHandler {
    override fun onMove(event: MouseMoveEvent, chartSupport: ChartSupport): EventConsumption {
      val coordinates = event.coordinates
      val target = coordinates?.let { interactionTargets.findTargetAt(it) }

      return handle(tool.onHover(target, coordinates, interactionState, configuration.stringsPlanningModel))
    }

    override fun onDown(event: MouseDownEvent, chartSupport: ChartSupport): EventConsumption {
      val target = interactionTargets.findTargetAt(event.coordinates)

      return handle(tool.onPress(target, event.coordinates, interactionState, configuration.stringsPlanningModel))
    }
  }.enabledWhen { configuration.planningEnabled }

  override fun layout(paintingContext: LayerPaintingContext) {
    super.layout(paintingContext)

    interactionTargets.beginLayout()

    if (configuration.planningEnabled.not()) {
      return
    }

    //A deleted module stays interactive as long as it is still wired into a string
    configuration.roofPlanningModel.modules().visibleModules.fastForEach { module ->
      if (module.deleted.not() || configuration.stringsPlanningModel.containsModule(module)) {
        interactionTargets.register(module, HandleRole.Body, configuration.getBoundsForModule(module))
      }
    }
  }

  override fun initialize(paintingContext: LayerPaintingContext) {
    val chartSupport = paintingContext.chartSupport
    this.chartSupport = chartSupport

    //Update the mouse cursor depending on the state - tied to the chart-support lifecycle
    interactionStateProperty.consumeImmediately {
      chartSupport.cursor = tool.cursorFor(it)
      chartSupport.markAsDirty(DirtyReason.UiStateChanged)
    }.disposeOn(chartSupport)
  }

  override fun paint(paintingContext: LayerPaintingContext) {
    val gc = paintingContext.gc

    if (configuration.planningEnabled) {
      //Paint all module highlights
      configuration.roofPlanningModel.modules().fastForEachVisibleModule { module ->
        val plannedString = interactionState.plannedStringOf(module, configuration.stringsPlanningModel)
        val hovered = interactionState.isHovered(module)

        if (plannedString == null && hovered.not()) {
          return@fastForEachVisibleModule
        }

        val moduleBounds = configuration.getBoundsForModule(module)

        plannedString?.let {
          gc.fillAndStroke(configuration.selectionHighlightStyles.valueAt(it.globalStringIndex.value))
          gc.fillRect(moduleBounds)
        }

        if (hovered) {
          gc.fillAndStroke(configuration.hoverHighlightStyle)
          gc.fillRect(moduleBounds)
        }
      }
    }

    val moduleStringsCoordinates = paintingVariables().stringCoordinates
    val roofStringsConfiguration = configuration.stringsPlanningModel.roofStringsConfiguration

    moduleStringsCoordinates.fastForEachWithIndex { stringIndex, stringCoordinatesBuffers ->
      val roofString = roofStringsConfiguration.roofStrings[stringIndex]
      val stringColorIndex = roofString.globalStringIndex
      val stringColor = configuration.stringColors.valueAt(stringColorIndex.value)
      gc.beginPath()

      stringCoordinatesBuffers.fastForEachIndexed { _, moduleCoordinatesX, moduleCoordinatesY ->
        gc.lineTo(moduleCoordinatesX, moduleCoordinatesY)
      }

      interactionState.pendingPoint?.let { planningLocation ->
        if (configuration.stringsPlanningModel.selectedString == roofString) gc.lineTo(planningLocation)
      }

      gc.stroke(stringColor)
      gc.lineWidth = 5.0
      gc.stroke()
    }

    val moduleWidths = paintingVariables().moduleWidths
    val moduleLabels = paintingVariables().moduleLabels
    val moduleLabelsBounds = paintingVariables().moduleLabelsBounds
    moduleStringsCoordinates.fastForEachWithIndex { stringIndex, stringCoordinates ->
      val roofString = roofStringsConfiguration.roofStrings[stringIndex]

      val stringColorIndex = roofString.globalStringIndex.value
      val labelBoxStyle = configuration.stringLabelBoxStyles.valueAt(stringColorIndex)
      val textColor = configuration.stringLabelBoxTextColors.valueAt(stringColorIndex)

      stringCoordinates.fastForEachIndexed { moduleIndexInString, moduleCoordinatesX, moduleCoordinatesY ->
        val moduleWidth = moduleWidths[stringIndex][moduleIndexInString]
        val moduleLabel = moduleLabels[stringIndex][moduleIndexInString]
        val moduleWidthToApplyScaling = 60.0

        gc.saved {
          gc.translate(moduleCoordinatesX, moduleCoordinatesY)
          val signFont = if (moduleWidth >= moduleWidthToApplyScaling) {
            configuration.fontStringEnd
          } else {
            FontDescriptorFragment(families = configuration.fontStringEnd.families, size = FontSize((configuration.fontStringEnd.size?.size ?: 25.0) * (moduleWidth / moduleWidthToApplyScaling).pow(2)))
          }
          val offset = if (moduleWidth >= moduleWidthToApplyScaling) 30.0 else 30.0 * (moduleWidth / moduleWidthToApplyScaling).pow(2)
          gc.font(signFont)
          if (moduleIndexInString == 0) {
            gc.translate(0.0, -offset)
            val sign = if (roofString.previousModulesCount == 0) "+" else "${'A' + stringColorIndex}"
            gc.paintTextBox(line = sign, anchorDirection = Direction.Center, boxStyle = labelBoxStyle, textColor = textColor)
            gc.translate(0.0, offset)
          }
          if (moduleIndexInString == stringCoordinates.size - 1) {
            gc.translate(0.0, offset)
            val sign = if (roofString.followingModulesCount == 0) "–" else "${'A' + stringColorIndex}"
            gc.paintTextBox(line = sign, anchorDirection = Direction.Center, boxStyle = labelBoxStyle, textColor = textColor)
            gc.translate(0.0, -offset)
          }

          val labelFont = if (moduleWidth >= moduleWidthToApplyScaling) {
            configuration.font
          } else {
            FontDescriptorFragment(families = configuration.font.families, size = FontSize((configuration.font.size?.size ?: 15.0) * (moduleWidth / moduleWidthToApplyScaling).pow(2)))
          }
          gc.font(labelFont)
          val boundingBox = gc.paintTextBox(line = moduleLabel, anchorDirection = Direction.Center, boxStyle = labelBoxStyle, textColor = textColor)
          moduleLabelsBounds[stringIndex][moduleIndexInString] = boundingBox
        }
      }
    }

  }

  override fun paintingVariables(): PvStringsPlanningPaintingVariables {
    return paintingVariables
  }

  private val paintingVariables: PvStringsPlanningPaintingVariables = object : PvStringsPlanningPaintingVariables {

    override val stringCoordinates: @Window CoordinatesMulti2DBuffer = CoordinatesMulti2DBuffer()

    override val moduleWidths: DoubleMulti2DBuffer = DoubleMulti2DBuffer()

    override val moduleLabels: StringMulti2DBuffer = StringMulti2DBuffer()

    override val moduleLabelsBounds: BoundsMulti2DBuffer = BoundsMulti2DBuffer()

    override fun calculate(paintingContext: LayerPaintingContext) {
      val roofStringsConfiguration = configuration.stringsPlanningModel.roofStringsConfiguration
      val stringCount = roofStringsConfiguration.count

      stringCoordinates.prepare(stringCount) //ensure array sizes
      moduleWidths.prepare(stringCount) //ensure array sizes
      moduleLabels.prepare(stringCount) //ensure array sizes
      moduleLabelsBounds.prepare(stringCount) //ensure array sizes
      roofStringsConfiguration.roofStrings.fastForEachIndexed { stringIndex, roofString ->
        val moduleCoordinatesBuffer = stringCoordinates[stringIndex]
        moduleCoordinatesBuffer.prepare(roofString.count) //ensure array sizes
        val moduleWidthsBuffer = moduleWidths[stringIndex]
        moduleWidthsBuffer.prepare(roofString.count) //ensure array sizes
        val moduleLabelsBuffer = moduleLabels[stringIndex]
        moduleLabelsBuffer.prepare(roofString.count) //ensure array sizes

        roofString.modules.fastForEachIndexed { moduleIndex, module ->
          val moduleBounds = configuration.getBoundsForModule(module)
          val centerX = moduleBounds.getX() + moduleBounds.getWidth() / 2.0
          val centerY = moduleBounds.getY() + moduleBounds.getHeight() / 2.0

          moduleWidthsBuffer[moduleIndex] = moduleBounds.widthAbs
          moduleCoordinatesBuffer.set(moduleIndex, centerX, centerY)

          val moduleLabel = "${roofString.inverterIndex + 1}.${roofString.mpptInputIndex + 1}.${roofString.stringIndex.value + 1}.${roofString.previousModulesCount + moduleIndex + 1}"
          moduleLabelsBuffer[moduleIndex] = moduleLabel
        }
      }
    }
  }


  @ConfigurationDsl
  class Configuration(
    val stringsPlanningModel: PvStringsPlanningModel,

    /**
     * Paints the roof below this layer and owns the geometry every module region is derived from.
     */
    val roofPlanningLayer: PvRoofPlanningLayer,
  ) {
    val roofPlanningModel: PvRoofPlanningModel
      get() = roofPlanningLayer.configuration.model

    /**
     * The current mode. Shared with [roofPlanningLayer] so both layers can never disagree about it.
     */
    var mode: Mode
      get() = roofPlanningLayer.configuration.mode
      set(value) {
        roofPlanningLayer.configuration.mode = value
      }

    val planningEnabled: Boolean
      get() = roofPlanningLayer.configuration.planningEnabled

    fun getBoundsForModule(module: Module): @Window Rectangle = roofPlanningLayer.getBoundsForModule(module)

    /**
     * The style when the mouse over an element
     */
    val hoverHighlightStyle: FillAndStrokeStyle = FillAndStrokeStyle(Color.web("#75b72633"), Color.web("#75b726"))

    /**
     * Used to style the string labels on each module
     */
    var stringLabelBoxStyles: MultiProvider<StringIndex, BoxStyle> = defaultStringLabelBoxStyles

    var stringColors: MultiProvider<StringIndex, RgbaColor> = defaultStringColors

    var stringLabelBoxTextColors: MultiProvider<StringIndex, Color> = defaultStringLabelBoxTextColors

    val selectionHighlightStyles: MultiProvider<StringIndex, FillAndStrokeStyle> = MultiProvider.invoke { index ->
      val color = stringLabelBoxStyles.valueAt(index).fill?.invoke()?.toRgba() ?: Color.black()
      FillAndStrokeStyle(color.withAlpha(0.3), color)
    }

    val font: FontDescriptorFragment = FontDescriptorFragment(familyConfiguration = LizergyDesign.defaultFontFamily, size = FontSize(15.0))
    val fontStringEnd: FontDescriptorFragment = FontDescriptorFragment(familyConfiguration = LizergyDesign.defaultFontFamily, size = FontSize(25.0))


    companion object {
      val defaultStringLabelBoxStyles: MultiProvider<StringIndex, BoxStyle> = MultiProvider.forListModulo(
        listOf(
          BoxStyle(fill = Color.yellow, borderColor = null, radii = BorderRadius.all2, shadow = null),
          BoxStyle(fill = Color.red, borderColor = null, radii = BorderRadius.all2, shadow = null),
          BoxStyle(fill = Color.cyan, borderColor = null, radii = BorderRadius.all2, shadow = null),
          BoxStyle(fill = Color.green, borderColor = null, radii = BorderRadius.all2, shadow = null),
          BoxStyle(fill = Color.blue, borderColor = null, radii = BorderRadius.all2, shadow = null),
          BoxStyle(fill = Color.magenta, borderColor = null, radii = BorderRadius.all2, shadow = null),
          BoxStyle(fill = Color.brown, borderColor = null, radii = BorderRadius.all2, shadow = null),
          BoxStyle(fill = Color.pink, borderColor = null, radii = BorderRadius.all2, shadow = null),
          BoxStyle(fill = Color.orange, borderColor = null, radii = BorderRadius.all2, shadow = null),
          BoxStyle(fill = Color.purple, borderColor = null, radii = BorderRadius.all2, shadow = null),
        ),
      )

      val defaultStringColors: MultiProvider<StringIndex, RgbaColor> = MultiProvider.invoke { index -> defaultStringLabelBoxStyles.valueAt(index).fill?.invoke()?.toRgba() ?: Color.black() }

      val defaultStringLabelBoxTextColors: MultiProvider<StringIndex, Color> = MultiProvider.forListModulo(
        listOf(
          Color.black(),
          Color.black(),
          Color.black(),
          Color.white(),
          Color.white(),
          Color.black(),
          Color.white(),
          Color.black(),
          Color.black(),
          Color.white(),
        )
      )
    }
  }
}
